import { useState, useEffect } from "react";
import { FileText, Loader2, CheckCircle2, AlertCircle, Wrench, RefreshCw, ClipboardList, Banknote, Download, User, X, ExternalLink, FileSignature } from "lucide-react";
import { toast } from "sonner";
import api, { formatPriceExact as formatPrice } from "../../../lib/api";
import { previewPdfResponse, customerRecipient } from "../../../lib/pdfPreview";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { useAutosaveOnBlur, AutosaveBadge } from "./useAutosaveOnBlur";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import { DispoGearButton, AutoInvoiceSettingsDialog } from "./DispoAutomationSettings";

const ORDER_STATUS_COLORS = {
    "Erledigt": "bg-emerald-100 text-emerald-700 border-emerald-300",
    "Berechnet": "bg-sky-100 text-sky-700 border-sky-300",
    "In Arbeit": "bg-amber-100 text-amber-700 border-amber-300",
    "Storniert": "bg-red-100 text-red-700 border-red-300",
};

// ---------- Rechnung schreiben ------------------------------------------------
export function InvoiceModal({ listingId, existing, onBack, onClose, onSaved }) {
    const [autoSettingsOpen, setAutoSettingsOpen] = useState(false);
    const [form, setForm] = useState({
        gross_amount: existing?.gross_amount || "",
        vat_rate: existing?.vat_rate || 19,
        via_zweiradhaendler: existing?.via_zweiradhaendler ?? true,
        note: existing?.note || "",
    });
    const [saving, setSaving] = useState(false);
    // Jun-2026 (User-Wunsch) — erledigten Werkstattauftrag mitabrechnen.
    const [workshop, setWorkshop] = useState(null);
    const [billWorkshop, setBillWorkshop] = useState(false);
    // Jun-2026 (User-Wunsch) — interne Aufträge (Nr./Betrag/Status) anzeigen.
    const [internalOrders, setInternalOrders] = useState([]);
    const [billingId, setBillingId] = useState(null);
    // Jun-2026 (User-Wunsch) — Vorschau der nächsten fortlaufenden Rechnungsnummer.
    const [invNumberPreview, setInvNumberPreview] = useState("");
    // Jun-2026 (User-Wunsch · §1 ZweiradhdlVO) — verkaufender Händler (registrierter
    // User) per Suche wählen → Transaktionscode wird übernommen.
    const [zwh, setZwh] = useState({
        dealer_user_id: existing?.zwh_dealer_user_id || "",
        dealer_name: existing?.zwh_dealer_name || "",
        transaction_code: existing?.zwh_transaction_code || "",
    });
    const [userQuery, setUserQuery] = useState("");
    const [userResults, setUserResults] = useState([]);
    const [userSearching, setUserSearching] = useState(false);
    const [userOpen, setUserOpen] = useState(false);
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const selectUser = (u) => {
        setZwh({ dealer_user_id: u.id, dealer_name: u.name, transaction_code: u.transaction_code || "" });
        setUserQuery(""); setUserResults([]); setUserOpen(false);
        if (!u.transaction_code) toast.message("Für diesen User ist kein Transaktionscode hinterlegt.");
    };
    const clearUser = () => setZwh({ dealer_user_id: "", dealer_name: "", transaction_code: "" });

    // Jun-2026 (User-Wunsch) — interne Aufträge separat (intern) abrechnen.
    const reloadInternal = async () => {
        try { const r = await api.get(`/listings/${listingId}/dispo/internal-orders`); setInternalOrders(r.data?.orders || []); } catch { /* */ }
    };
    const billInternal = async (orderId) => {
        setBillingId(orderId);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/internal-orders/${orderId}/bill-internal`);
            toast.success(`Intern abgerechnet · ${r.data.internal_invoice_number}`);
            await reloadInternal();
        } catch (e) { toast.error(e.response?.data?.detail || "Interne Abrechnung fehlgeschlagen"); }
        finally { setBillingId(null); }
    };
    const openInternalPdf = async (orderId) => {
        try {
            const r = await api.get(`/listings/${listingId}/dispo/internal-orders/${orderId}/invoice.pdf`, { responseType: "blob" });
            previewPdfResponse(r, "Interner-Beleg.pdf");
        } catch { toast.error("Interner Beleg konnte nicht geöffnet werden"); }
    };
    // Jun-2026 (User-Wunsch · c) — E-Rechnung herunterladen (PDF / XRechnung-XML / ZUGFeRD-Hybrid-PDF).
    const [docBusy, setDocBusy] = useState("");
    const openDispoDoc = async (kind) => {
        const cfg = {
            pdf: { path: "invoice.pdf", mime: "application/pdf", dl: false },
            xrechnung: { path: "xrechnung.xml", mime: "application/xml", dl: `XRechnung_${existing?.invoice_number || ""}.xml` },
            zugferd: { path: "zugferd.pdf", mime: "application/pdf", dl: false },
        }[kind];
        setDocBusy(kind);
        try {
            const r = await api.get(`/listings/${listingId}/dispo/${cfg.path}`, { responseType: "blob" });
            if (cfg.dl) {
                const url = URL.createObjectURL(new Blob([r.data], { type: cfg.mime }));
                const a = document.createElement("a");
                a.href = url; a.download = cfg.dl; document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 5000);
            } else {
                previewPdfResponse(r, kind === "zugferd" ? `ZUGFeRD_${existing?.invoice_number || "Rechnung"}.pdf` : `Rechnung_${existing?.invoice_number || ""}.pdf`, { recipient: customerRecipient(existing?.customer) });
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Dokument konnte nicht erzeugt werden"); }
        finally { setDocBusy(""); }
    };

    const [emailBusy, setEmailBusy] = useState(false);
    // Jul-2026 (User-Wunsch) — Versand-Vorschau statt window.confirm: Empfänger,
    // Anhänge und editierbarer Begleittext vor dem Senden prüfen.
    const [emailAsk, setEmailAsk] = useState(false);
    const [emailMsg, setEmailMsg] = useState("");
    const [emailedInfo, setEmailedInfo] = useState(
        existing?.emailed_at ? { at: existing.emailed_at, to: existing.emailed_to } : null);
    const customerEmail = existing?.customer?.email || "";
    const customerName = [existing?.customer?.first_name, existing?.customer?.last_name].filter(Boolean).join(" ")
        || existing?.customer?.company_name || "";
    const openEmailPreview = () => {
        setEmailMsg(
            `Hallo ${customerName || "Kunde"},\n\nvielen Dank für Ihren Einkauf. `
            + "Bei Rückfragen wenden Sie sich bitte direkt an uns.\n\nMit freundlichen Grüßen");
        setEmailAsk(true);
    };
    const sendInvoiceEmail = async () => {
        setEmailBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/invoice/send-email`,
                { custom_message: emailMsg.trim() || null });
            if (r.data?.mocked) toast.success(`Versand vorbereitet (Demo-Modus, kein Live-Mailversand) an ${r.data.sent_to}`);
            else toast.success(`Rechnung an ${r.data.sent_to} gesendet`);
            setEmailedInfo({ at: new Date().toISOString(), to: r.data?.sent_to });
            setEmailAsk(false);
        } catch (e) { toast.error(e.response?.data?.detail || "E-Mail-Versand fehlgeschlagen"); }
        finally { setEmailBusy(false); }
    };

    useEffect(() => {
        let alive = true;
        api.get(`/listings/${listingId}/dispo/workshop-order`).then((r) => {
            if (!alive) return;
            const wo = r.data || {};
            if (wo.completed && Number(wo.gross_total) > 0) {
                setWorkshop(wo);
                setBillWorkshop(true); // vormarkiert
            }
        }).catch(() => { /* kein Werkstattauftrag */ });
        // Jun-2026 (User-Wunsch) — interne Aufträge (Werkstatt) zu diesem Fahrzeug laden.
        api.get(`/listings/${listingId}/dispo/internal-orders`).then((r) => {
            if (!alive) return;
            setInternalOrders((r.data?.orders) || []);
        }).catch(() => setInternalOrders([]));
        // Jun-2026 (User-Wunsch) — Transaktionsbetrag des Händlers (Verkaufspreis aus
        // der Disposition) automatisch als Bruttobetrag vorbelegen, manuell änderbar.
        if (!existing?.invoice_number) {
            api.get(`/listings/${listingId}/dispo/payment`).then((r) => {
                if (!alive) return;
                const fp = Number(r.data?.final_price || 0);
                if (fp > 0) setForm((p) => (p.gross_amount === "" || p.gross_amount == null) ? { ...p, gross_amount: fp } : p);
            }).catch(() => {});
        }
        // Jun-2026 (User-Wunsch) — Entwurfsdaten vorbelegen (sofern noch keine
        // offizielle Rechnung existiert).
        if (!existing?.invoice_number) {
            api.get(`/listings/${listingId}/dispo/invoice/draft`).then((r) => {
                if (!alive) return;
                const d = r.data || {};
                setInvNumberPreview(d.assigned_invoice_number || d.next_invoice_number || "");
                if (d && (d.gross_amount != null || d.note || d.via_zweiradhaendler != null)) {
                    setForm((p) => ({
                        ...p,
                        gross_amount: d.gross_amount != null ? d.gross_amount : p.gross_amount,
                        vat_rate: d.vat_rate != null ? d.vat_rate : p.vat_rate,
                        via_zweiradhaendler: d.via_zweiradhaendler != null ? d.via_zweiradhaendler : p.via_zweiradhaendler,
                        note: d.note || p.note,
                    }));
                    if (d.zwh_dealer_user_id || d.zwh_transaction_code) {
                        setZwh({ dealer_user_id: d.zwh_dealer_user_id || "", dealer_name: d.zwh_dealer_name || "", transaction_code: d.zwh_transaction_code || "" });
                    }
                }
            }).catch(() => {});
        }
        return () => { alive = false; };
    }, [listingId, existing]);

    // Jun-2026 (User-Wunsch) — debounced User-Suche (alle registrierten User).
    useEffect(() => {
        if (form.via_zweiradhaendler !== true) return undefined;
        const qq = userQuery.trim();
        if (qq.length < 2) { setUserResults([]); setUserOpen(false); return undefined; }
        let alive = true;
        setUserSearching(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/dealer/users/search?q=${encodeURIComponent(qq)}`);
                if (alive) { setUserResults(r.data?.users || []); setUserOpen(true); }
            } catch { if (alive) setUserResults([]); }
            finally { if (alive) setUserSearching(false); }
        }, 300);
        return () => { alive = false; clearTimeout(t); };
    }, [userQuery, form.via_zweiradhaendler]);

    // Entwurf (NICHT die finale Rechnung) automatisch speichern.
    const saveDraft = async ({ silent = false } = {}) => {
        try {
            await api.patch(`/listings/${listingId}/dispo/invoice/draft`, {
                gross_amount: form.gross_amount === "" ? null : Number(form.gross_amount),
                vat_rate: Number(form.vat_rate),
                via_zweiradhaendler: form.via_zweiradhaendler,
                zwh_dealer_user_id: zwh.dealer_user_id || null,
                zwh_dealer_name: zwh.dealer_name || null,
                zwh_transaction_code: zwh.transaction_code || null,
                note: form.note,
            });
            if (!silent) toast.success("Rechnungs-Entwurf gespeichert");
            autosave.markSaved();
            return true;
        } catch (e) {
            if (!silent) toast.error(e.response?.data?.detail || "Entwurf konnte nicht gespeichert werden");
            return false;
        }
    };
    const autosave = useAutosaveOnBlur(
        () => saveDraft({ silent: true }),
        () => JSON.stringify({ form, zwh }),
        { enabled: !existing?.invoice_number },
    );

    const woGross = billWorkshop && workshop ? Number(workshop.gross_total) : 0;
    const totalGross = (Number(form.gross_amount) || 0) + woGross;

    const [previewBusy, setPreviewBusy] = useState(false);
    // Jul-2026 (User-Wunsch) — 2-stufig: „Rechnung erstellen" zeigt ERST die
    // Vorschau, danach die Wahl „Jetzt verbindlich erstellen" oder „Abbrechen".
    const [confirmStage, setConfirmStage] = useState(false);

    const validate = () => {
        if (!form.gross_amount || Number(form.gross_amount) <= 0) { toast.error("Bruttobetrag muss > 0 sein"); return false; }
        if (form.via_zweiradhaendler === null) { toast.error('Pflicht: "Über Zweiradhändler-Aktivität verkauft?" (Ja/Nein)'); return false; }
        if (form.via_zweiradhaendler === true && !zwh.dealer_user_id) { toast.error("Bitte den verkaufenden Händler (registrierter User) auswählen."); return false; }
        return true;
    };

    const startCreate = async () => {
        if (!validate()) return;
        await saveDraft({ silent: true });
        await previewDraft();
        setConfirmStage(true);
    };

    const previewDraft = async () => {
        if (!form.gross_amount || Number(form.gross_amount) <= 0) { toast.error("Bruttobetrag muss > 0 sein für die Vorschau"); return; }
        setPreviewBusy(true);
        try {
            const note = billWorkshop && workshop
                ? `${form.note ? form.note + "\n" : ""}Werkstattauftrag ${workshop.order_number || ""} (${formatPrice(workshop.gross_total)}) mitabgerechnet.`.trim()
                : form.note;
            const r = await api.post(`/listings/${listingId}/dispo/invoice/preview.pdf`, {
                gross_amount: totalGross,
                vat_rate: Number(form.vat_rate),
                note,
            }, { responseType: "blob" });
            previewPdfResponse(r, "Rechnung_Vorschau.pdf");
        } catch (e) { toast.error(e.response?.data?.detail || "Vorschau konnte nicht erzeugt werden"); }
        finally { setPreviewBusy(false); }
    };

    const submit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const note = billWorkshop && workshop
                ? `${form.note ? form.note + "\n" : ""}Werkstattauftrag ${workshop.order_number || ""} (${formatPrice(workshop.gross_total)}) mitabgerechnet.`.trim()
                : form.note;
            await api.post(`/listings/${listingId}/dispo/invoice`, {
                ...form,
                gross_amount: totalGross,
                vat_rate: Number(form.vat_rate),
                note,
                zwh_dealer_user_id: zwh.dealer_user_id || null,
                zwh_dealer_name: zwh.dealer_name || null,
                zwh_transaction_code: zwh.transaction_code || null,
                workshop_order_number: billWorkshop && workshop ? workshop.order_number : null,
                workshop_order_gross: billWorkshop && workshop ? Number(workshop.gross_total) : null,
            });
            toast.success("Rechnung erstellt");
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erstellung fehlgeschlagen");
        } finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="w-[96vw] max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="dispo-modal-invoice" onBlur={autosave.onBlur}>
                <FeatureGuideTriangle guideKey="invoice" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Rechnung schreiben
                    <DispoGearButton onClick={() => setAutoSettingsOpen(true)} testid="invoice-auto-settings-gear" title="Automatische Rechnung — Regeln & Status" />
                </DialogTitle></DialogHeader>
                <AutoInvoiceSettingsDialog open={autoSettingsOpen} onClose={() => setAutoSettingsOpen(false)} listingId={listingId} />
                {!existing?.invoice_number && <AutosaveBadge status={autosave.status} savedAt={autosave.savedAt} />}
                {/* Jul-2026 (User-Wunsch) — 2-spaltiges, kompaktes Layout. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-2 mt-1">
                <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Rechnungs-Nr</Label>
                        <div className="mt-1 h-10 flex items-center px-2.5 rounded-sm border border-dashed border-gray-300 bg-gray-50 font-mono-num text-sm text-gray-700" data-testid="dispo-inv-number" title="Wird systemseitig fortlaufend vergeben">
                            {existing?.invoice_number || invNumberPreview || "Automatisch · fortlaufend (wird beim Erstellen vergeben)"}
                        </div>
                    </div>
                    <div><Label className="text-xs">Brutto Fahrzeug (€) * <span className="text-gray-400 font-normal">· aus Transaktion vorbelegt</span></Label><Input type="number" min="0" step="0.01" value={form.gross_amount} onChange={(e) => update("gross_amount", e.target.value)} className="font-mono-num" data-testid="dispo-inv-gross" /></div>
                    <div><Label className="text-xs">USt.-Satz (%)</Label><Input type="number" min="0" max="30" value={form.vat_rate} onChange={(e) => update("vat_rate", e.target.value)} className="font-mono-num" data-testid="dispo-inv-vat" /></div>
                </div>

                {/* Jun-2026 (User-Wunsch) — interne Aufträge zu diesem Fahrzeug: Nr., Betrag, Status. */}
                <div className="mt-3" data-testid="dispo-inv-internal-orders">
                    <Label className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5 text-gray-500" /> Interne Aufträge</Label>
                    {internalOrders.length === 0 ? (
                        <div className="text-sm text-gray-400 mt-1 italic" data-testid="dispo-inv-internal-orders-empty">keine</div>
                    ) : (
                        <div className="border border-gray-200 rounded-sm divide-y divide-gray-100 mt-1">
                            {internalOrders.map((o) => (
                                <div key={o.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm" data-testid={`dispo-inv-internal-order-${o.order_number}`}>
                                    <button type="button" onClick={() => window.open(`/dealer/workshop-orders?focus=${o.id}`, "_blank", "noopener")}
                                        className="font-mono-num font-bold text-sky-700 hover:underline shrink-0 inline-flex items-center gap-1" title="Internen Auftrag öffnen"
                                        data-testid={`dispo-inv-internal-open-${o.order_number}`}>
                                        {o.order_number}<ExternalLink className="w-3 h-3" />
                                    </button>
                                    <span className="font-mono-num text-gray-700 shrink-0">{formatPrice(o.gross_total)}</span>
                                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-sm border px-1.5 py-0.5 shrink-0 ${ORDER_STATUS_COLORS[o.status_label] || "bg-gray-100 text-gray-600 border-gray-300"}`}>{o.status_label}</span>
                                    <div className="flex-1" />
                                    {o.can_bill_internal && (
                                        <Button type="button" size="sm" variant="outline" onClick={() => billInternal(o.id)} disabled={billingId === o.id}
                                            className="h-7 text-[11px] border-2 shrink-0" data-testid={`dispo-inv-bill-internal-${o.order_number}`}>
                                            {billingId === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Banknote className="w-3.5 h-3.5 mr-1" /> Intern abrechnen</>}
                                        </Button>
                                    )}
                                    {o.billed_internal && (
                                        <span className="flex items-center gap-1.5 shrink-0" data-testid={`dispo-inv-internal-billed-${o.order_number}`}>
                                            <span className="text-[10px] font-mono-num font-bold text-emerald-700">{o.internal_invoice_number}</span>
                                            <Button type="button" size="sm" variant="outline" onClick={() => openInternalPdf(o.id)}
                                                className="h-7 text-[11px] border-2" data-testid={`dispo-inv-internal-pdf-${o.order_number}`}>
                                                <Download className="w-3.5 h-3.5 mr-1" /> Beleg
                                            </Button>
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Jun-2026 (User-Wunsch) — erledigten Werkstattauftrag mitabrechnen */}
                {workshop && (
                    <label className="flex items-start gap-2.5 bg-sky-50 border-2 border-sky-300 rounded-sm p-3 mt-3 cursor-pointer" data-testid="dispo-inv-workshop-block">
                        <input
                            type="checkbox"
                            checked={billWorkshop}
                            onChange={(e) => setBillWorkshop(e.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-sky-600 shrink-0"
                            data-testid="dispo-inv-workshop-checkbox"
                        />
                        <span className="text-sm text-sky-900 flex-1">
                            <span className="font-bold inline-flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> Werkstattauftrag {workshop.order_number || ""} mitabrechnen</span>
                            <span className="block text-xs mt-0.5">Erledigt · Betrag <span className="font-mono-num font-bold">{formatPrice(workshop.gross_total)}</span> wird als separate Position zur Rechnung addiert.</span>
                        </span>
                    </label>
                )}

                </div>{/* /linke Spalte */}
                <div className="space-y-3">
                {/* Pflicht-Checkbox „über Zweiradhändler-Aktivität verkauft" */}
                <div className="bg-amber-50 border-2 border-amber-300 rounded-sm p-3" data-testid="dispo-inv-zwh-block">
                    <div className="flex items-center gap-1.5 font-bold text-sm mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-700" /> Pflichtangabe (§1 ZweiradhdlVO)
                    </div>
                    <div className="text-xs text-gray-700 mb-2">Wurde das Fahrzeug über die Zweiradhändler-Aktivität verkauft?</div>
                    {/* Jun-2026 (User-Wunsch) — Ja/Nein und (bei „Ja") die Händlersuche
                        stehen in EINER Zeile. */}
                    <div className="flex flex-wrap items-center gap-2">
                        <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border-2 cursor-pointer ${form.via_zweiradhaendler === true ? "bg-emerald-100 border-emerald-500" : "border-gray-300"}`}>
                            <input type="radio" name="zwh" checked={form.via_zweiradhaendler === true} onChange={() => update("via_zweiradhaendler", true)} className="accent-emerald-600" data-testid="dispo-inv-zwh-yes" />
                            <span className="text-sm font-bold">Ja</span>
                        </label>
                        <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border-2 cursor-pointer ${form.via_zweiradhaendler === false ? "bg-gray-100 border-gray-500" : "border-gray-300"}`}>
                            <input type="radio" name="zwh" checked={form.via_zweiradhaendler === false} onChange={() => update("via_zweiradhaendler", false)} className="accent-gray-600" data-testid="dispo-inv-zwh-no" />
                            <span className="text-sm font-bold">Nein</span>
                        </label>
                        {/* bei „Ja": verkaufenden Händler (registrierter User) inline daneben wählen
                            → dessen Transaktionscode (Dashboard → Marketing) wird übernommen. */}
                        {form.via_zweiradhaendler === true && (
                            <div className="flex items-center gap-2 flex-1 min-w-[260px]" data-testid="dispo-inv-zwh-dealer">
                                <span className="text-[11px] text-gray-600 shrink-0">Verk. Händler:</span>
                                {zwh.dealer_user_id ? (
                                    <div className="flex items-center gap-2 bg-white border border-amber-300 rounded-sm px-2.5 py-1.5 flex-1 min-w-0" data-testid="dispo-inv-zwh-selected">
                                        <User className="w-4 h-4 text-amber-700 shrink-0" />
                                        <span className="text-sm font-bold text-gray-800 flex-1 min-w-0 truncate">{zwh.dealer_name}</span>
                                        {zwh.transaction_code
                                            ? <span className="font-mono-num text-xs font-bold bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm shrink-0" data-testid="dispo-inv-zwh-code">{zwh.transaction_code}</span>
                                            : <span className="text-[10px] text-red-600 font-bold shrink-0" data-testid="dispo-inv-zwh-code">kein Code hinterlegt</span>}
                                        <button type="button" onClick={clearUser} className="text-gray-400 hover:text-red-600 shrink-0" aria-label="Auswahl entfernen" data-testid="dispo-inv-zwh-clear"><X className="w-4 h-4" /></button>
                                    </div>
                                ) : (
                                    <div className="relative flex-1 min-w-0">
                                        <Input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} onFocus={() => userResults.length && setUserOpen(true)} placeholder="Name, Firma oder E-Mail suchen…" className="h-9 text-sm" data-testid="dispo-inv-zwh-search" />
                                        {userSearching && <Loader2 className="w-4 h-4 animate-spin absolute right-2.5 top-2.5 text-gray-400" />}
                                        {userOpen && userResults.length > 0 && (
                                            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg max-h-52 overflow-y-auto" data-testid="dispo-inv-zwh-results">
                                                {userResults.map((u) => (
                                                    <button type="button" key={u.id} onClick={() => selectUser(u)} className="w-full text-left px-2.5 py-1.5 hover:bg-amber-50 flex items-center gap-2 text-sm" data-testid={`dispo-inv-zwh-result-${u.id}`}>
                                                        <span className="flex-1 min-w-0 truncate">{u.name} <span className="text-[10px] text-gray-400 uppercase">{u.role}</span></span>
                                                        {u.transaction_code && <span className="font-mono-num text-[10px] text-gray-500 shrink-0">{u.transaction_code}</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {userQuery.trim().length >= 2 && !userSearching && userResults.length === 0 && (
                                            <div className="text-[11px] text-gray-400 mt-1" data-testid="dispo-inv-zwh-noresults">Keine Treffer.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {form.via_zweiradhaendler === true && (
                        <div className="text-[10px] text-gray-500 mt-1">Verkaufender Händler (registrierter User) — der Transaktionscode wird übernommen.</div>
                    )}
                </div>

                <div className="mt-2"><Label className="text-xs">Notiz</Label><Textarea rows={2} value={form.note} onChange={(e) => update("note", e.target.value)} data-testid="dispo-inv-note" /></div>
                <div className="flex items-center justify-between bg-[#111827] text-white rounded-sm px-3 py-2 mt-2" data-testid="dispo-inv-total">
                    <span className="text-xs font-bold uppercase tracking-wider">Rechnungsbetrag (brutto)</span>
                    <span className="text-lg font-black font-mono-num" data-testid="dispo-inv-total-value">{formatPrice(totalGross)}</span>
                </div>

                {/* Jun-2026 (User-Wunsch · c) — E-Rechnung (EN 16931): PDF + XRechnung-XML + ZUGFeRD-Hybrid-PDF.
                    Erst nach Rechnungserstellung (mit Nummer) verfügbar. Pflicht ab 2027 (DE B2B). */}
                {existing?.invoice_number && (
                    <div className="mt-3 border-2 border-emerald-300 bg-emerald-50 rounded-sm p-3" data-testid="dispo-inv-erechnung">
                        <div className="flex items-center gap-1.5 font-bold text-sm text-emerald-900 mb-1">
                            <FileSignature className="w-4 h-4" /> E-Rechnung (EN 16931 · Pflicht ab 2027)
                        </div>
                        <p className="text-[11px] text-emerald-700 mb-2">Strukturierte, maschinenlesbare Rechnung für Steuerberater / DATEV / ADDISON.</p>
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" size="sm" variant="outline" onClick={() => openDispoDoc("pdf")} disabled={!!docBusy} className="h-8 text-xs border-2 bg-white" data-testid="dispo-inv-dl-pdf">
                                {docBusy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileText className="w-3.5 h-3.5 mr-1" /> Rechnung (PDF)</>}
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => openDispoDoc("zugferd")} disabled={!!docBusy} className="h-8 text-xs border-2 bg-white" data-testid="dispo-inv-dl-zugferd">
                                {docBusy === "zugferd" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileSignature className="w-3.5 h-3.5 mr-1" /> ZUGFeRD-PDF</>}
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => openDispoDoc("xrechnung")} disabled={!!docBusy} className="h-8 text-xs border-2 bg-white" data-testid="dispo-inv-dl-xrechnung">
                                {docBusy === "xrechnung" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> XRechnung (XML)</>}
                            </Button>
                            <Button type="button" size="sm" onClick={openEmailPreview} disabled={emailBusy} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dispo-inv-send-email">
                                {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileSignature className="w-3.5 h-3.5 mr-1" /> Per E-Mail an Kunden</>}
                            </Button>
                        </div>
                        {/* Jul-2026 (User-Wunsch) — Versand-Vorschau vor dem E-Mail-Versand */}
                        {emailAsk && (
                            <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4" data-testid="dispo-inv-email-preview">
                                <div className="bg-white max-w-lg w-full rounded-sm shadow-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
                                    <div className="font-display font-black text-lg flex items-center gap-2">
                                        <FileSignature className="w-5 h-5 text-emerald-600" /> Rechnung per E-Mail senden
                                    </div>
                                    <div className="text-sm space-y-1.5">
                                        <div className="flex gap-2">
                                            <span className="text-gray-400 text-[10px] uppercase tracking-wider w-20 pt-0.5">Empfänger</span>
                                            <span className="font-bold" data-testid="dispo-inv-email-recipient">{customerEmail || "— keine E-Mail hinterlegt —"}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <span className="text-gray-400 text-[10px] uppercase tracking-wider w-20 pt-0.5">Betreff</span>
                                            <span>Ihre Rechnung {existing?.invoice_number || ""}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <span className="text-gray-400 text-[10px] uppercase tracking-wider w-20 pt-0.5">Anhänge</span>
                                            <span className="text-[12px]">Rechnung_{existing?.invoice_number || ""}.pdf · XRechnung (XML, E-Rechnung)</span>
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Begleittext (editierbar)</Label>
                                        <Textarea
                                            value={emailMsg}
                                            onChange={(e) => setEmailMsg(e.target.value)}
                                            className="rounded-sm mt-1 min-h-[110px] text-sm"
                                            data-testid="dispo-inv-email-message"
                                        />
                                        <p className="text-[10px] text-gray-400 mt-1">Der Hinweis auf die angehängte Rechnung mit Betrag wird automatisch ergänzt.</p>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button type="button" variant="outline" onClick={() => setEmailAsk(false)} className="rounded-sm text-[12px]" data-testid="dispo-inv-email-abort">Abbrechen</Button>
                                        <Button type="button" onClick={sendInvoiceEmail} disabled={emailBusy || !customerEmail}
                                            className="rounded-sm text-[12px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dispo-inv-email-confirm">
                                            {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Jetzt senden</>}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {emailedInfo && (
                            <div className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-emerald-800" data-testid="dispo-inv-emailed-status">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Gesendet am {new Date(emailedInfo.at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}
                                {emailedInfo.to ? ` an ${emailedInfo.to}` : ""}
                            </div>
                        )}
                    </div>
                )}
                </div>{/* /rechte Spalte */}
                </div>{/* /2-Spalten-Grid */}
                <DialogFooter className="items-center">
                    <DispoBackButton onBack={onBack} />
                    {!confirmStage ? (
                        <>
                            <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                            <Button onClick={startCreate} disabled={previewBusy || saving} data-testid="dispo-inv-submit">
                                {previewBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1" />Rechnung erstellen</>}
                            </Button>
                        </>
                    ) : (
                        <>
                            <span className="text-xs text-gray-600 font-bold mr-1" data-testid="dispo-inv-confirm-hint">Vorschau geprüft?</span>
                            <Button variant="outline" onClick={() => setConfirmStage(false)} data-testid="dispo-inv-confirm-cancel">Abbrechen</Button>
                            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="dispo-inv-confirm-create">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" />Jetzt verbindlich erstellen</>}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
